import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { BUCKET_URL } from "../_shared/utils/constants";
import {
  CategeryCreateBody,
  Category,
  Service,
  ServiceCreateBody,
  ServiceProvider,
  UnitOfMeasure,
} from "./interface";

@Injectable({
  providedIn: "root",
})
export class ServicesService {
  private services = new BehaviorSubject<Service[]>([]);
  private serviceCategories = new BehaviorSubject<Category[]>([]);
  private serviceProviders = new BehaviorSubject<ServiceProvider[]>([]);
  private unitsOfMeasure = new BehaviorSubject<UnitOfMeasure[]>([]);
  constructor(private http: HttpClient) {}

  getServices2(params: string): Observable<Service[]> {
    return this.http.get(`${environment.API_URL}api/v3/services`).pipe(
      map((res: any) => {
        return res.data.map((val) => {
          const startPrice = Math.min(...val.packaging.map((v) => v.price));
          return {
            ...val,
            icon: val.icon
              ? `${BUCKET_URL}${val.icon}`
              : "https://via.placeholder.com/150?text=admin.ezyagric.com",
            startPrice,
          };
        }) as Service[];
      }),
      tap((res) => {
        this.services.next(res);
      })
    );
  }

  getServices(params: string): Observable<Service[]> {
    return this.http.get(`${environment.API_URL_V3}/services/fetch_services`).pipe(
      map((res: any) => {
        console.log(res);
        return res.data.map((val) => {
          const startPrice = Math.min(...val.packaging.map((v) => v.price));
          return {
            ...val,
            icon: val.photo_url
              ? `${BUCKET_URL}${val.photo_url}`
              : "https://via.placeholder.com/150?text=admin.ezyagric.com",
            startPrice,
          };
        }) as Service[];
      }),
      tap((res) => {
        this.services.next(res);
      })
    );
  }

  getService(id: string): Observable<Service> {
    return this.http.get(`${environment.API_URL}api/v3/services/${id}`).pipe(
      map((res) => {
        return res as Service;
      })
    );
  }

  getServiceCategories(): Observable<Category[]> {
    return this.http
      .get(`${environment.API_URL}api/v3/service-categories`)
      .pipe(
        map((res: { data: Category[] }) => {
          const categories = res.data.map((item) => {
            return this.formatServiceCategoryData(item);
          });
          return categories;
        }),
        tap((vals) => {
          this.serviceCategories.next(vals);
        })
      );
  }

  private formatServiceCategoryData(
    item: Category
  ): Category & { services: number } {
    return {
      ...item,
      icon: item.icon
        ? `${BUCKET_URL}${item.icon}`
        : "https://via.placeholder.com/150?text=admin.ezyagric.com",
      services: 200,
    };
  }

  getServiceProviders() {
    return this.http.get(`${environment.API_URL}api/v3/service-providers`).pipe(
      map((res: { data: ServiceProvider[] }) => {
        return res.data.map((item) => {
          return {
            ...item,
            icon: item.icon
              ? `${BUCKET_URL}${item.icon}`
              : "https://via.placeholder.com/150?text=admin.ezyagric.com",
            services: 10,
            orders: 5,
            address: `${item.district!.name} ${item.district!.country}`,
          };
        });
      }),
      tap((res) => {
        this.serviceProviders.next(res);
      })
    );
  }

  deleteService(id: string) {
    return this.http.delete(`${environment.API_URL}api/v3/services/${id}`).pipe(
      tap((val) => {
        this.services.next(this.services.value.filter((v) => v.id != id));
      })
    );
  }

  get services$(): Observable<Service[]> {
    return this.services.asObservable();
  }

  get serviceProviders$(): Observable<ServiceProvider[]> {
    return this.serviceProviders.asObservable();
  }

  getServiceById(id: string): Service {
    return this.services.getValue().find((service) => service.id === id);
  }

  getServiceCategoryById(id: string): Category {
    return this.serviceCategories.getValue().find((cat) => cat.id === id);
  }

  getServiceProviderById(id: string): ServiceProvider {
    return this.serviceProviders
      .getValue()
      .find((provider) => provider.id === id);
  }

  public createServiceCategory(data: CategeryCreateBody) {
    return this.http
      .post(`${environment.API_URL}api/v3/service-categories`, data)
      .pipe(
        tap((cat: Category) => {
          this.serviceCategories.next([
            this.formatServiceCategoryData(cat),
            ...this.serviceCategories.value,
          ]);
        })
      );
  }

  public createUnitOfMeasure(data: UnitOfMeasure) {
    return this.http
      .post(`${environment.API_URL}api/v3/service-measure-units`, data)
      .pipe(
        tap((val: UnitOfMeasure) => {
          this.unitsOfMeasure.next([val, ...this.unitsOfMeasure.value]);
        })
      );
  }

  public createService(data: ServiceCreateBody) {
    return this.http.post(`${environment.API_URL}api/v3/services`, data).pipe(
      tap((service: Service) => {
        const startPrice = Math.min(...service.packaging.map((v) => v.price));
        const val = {
          ...service,
          icon: service.icon
            ? `${BUCKET_URL}${service.icon}`
            : "https://via.placeholder.com/150?text=admin.ezyagric.com",
          startPrice,
        };
        this.services.next([val, ...this.services.value]);
      })
    );
  }
  public createServiceProvider(data: ServiceProvider) {
    return this.http
      .post(`${environment.API_URL}api/v3/service-providers`, data)
      .pipe(
        tap((item: ServiceProvider) => {
          this.serviceProviders.next([
            {
              ...item,
              icon: item.icon
                ? `${BUCKET_URL}${item.icon}`
                : "https://via.placeholder.com/150?text=admin.ezyagric.com",
              services: 10,
              orders: 5,
              address: `${item.district.name}, ${item.district.country}`,
            },
            ...this.serviceProviders.value,
          ]);
        })
      );
  }
  public get serviceCategories$() {
    return this.serviceCategories.asObservable();
  }

  public deleteServiceCategory(id: string) {
    return this.http
      .delete(`${environment.API_URL}api/v3/service-categories/${id}`)
      .pipe(
        tap((val) => {
          this.serviceCategories.next(
            this.serviceCategories.value.filter((v) => v.id !== id)
          );
        })
      );
  }

  public publishServiceCategory(id: string, published: boolean) {
    return this.http
      .patch(`${environment.API_URL}api/v3/service-categories/${id}`, {
        published,
      })
      .pipe(
        tap((val: Category) => {
          this.serviceCategories.next(
            this.serviceCategories.value.map((v) => {
              if (v.id === id) {
                return this.formatServiceCategoryData(val);
              }
              return v;
            })
          );
        })
      );
  }

  public updateServiceCategory(id: string, data: CategeryCreateBody) {
    return this.http
      .patch(`${environment.API_URL}api/v3/service-categories/${id}`, data)
      .pipe(
        tap((val: Category) => {
          this.serviceCategories.next(
            this.serviceCategories.value.map((v) => {
              if (v.id === id) {
                return this.formatServiceCategoryData(val);
              }
              return v;
            })
          );
        })
      );
  }

  public updateServiceProvider(id: string, data: Partial<ServiceProvider>) {
    return this.http
      .patch(`${environment.API_URL}api/v3/service-providers/${id}`, data)
      .pipe(
        tap((val: ServiceProvider) => {
          this.serviceProviders.next(
            this.serviceProviders.value.map((v) => {
              if (v.id === id) {
                return {
                  ...val,
                  icon: val.icon
                    ? `${BUCKET_URL}${val.icon}`
                    : "https://via.placeholder.com/150?text=admin.ezyagric.com",
                  services: 10,
                  orders: 5,
                  address: `${val.district.name}, ${val.district.country}`,
                };
              }
              return v;
            })
          );
        })
      );
  }

  public updateService(id: string, data: Partial<Service>) {
    return this.http
      .patch(`${environment.API_URL}api/v3/services/${id}`, data)
      .pipe(
        tap((val: Service) => {
          this.services.next(
            this.services.value.map((v) => {
              if (v.id === id) {
                const startPrice = Math.min(
                  ...val.packaging.map((v) => v.price)
                );
                return {
                  ...val,
                  icon: val.icon
                    ? `${BUCKET_URL}${val.icon}`
                    : "https://via.placeholder.com/150?text=admin.ezyagric.com",
                  startPrice,
                };
              }
              return v;
            })
          );
        })
      );
  }

  public getUnitsOfMeasure() {
    return this.http
      .get(`${environment.API_URL}api/v3/service-measure-units`)
      .pipe(
        map((res: { data: UnitOfMeasure[] }) => {
          return res.data;
        }),
        tap((res) => {
          this.unitsOfMeasure.next(res);
        })
      );
  }

  public get unitsOfMeasure$() {
    return this.unitsOfMeasure.asObservable();
  }

  public getunitOfMeasureById(id: string) {
    return this.unitsOfMeasure.getValue().find((v) => v.id === id);
  }
}
